import {
  FieldCredential,
  Module,
  ModuleCredential,
  ModuleMeta,
  CommissionMetaParams,
  CredentialType,
  CredentialContextType,
  CredentialContextProviderProps,
  ModuleParams,
  HasCredential,
} from "./types";
import get from "lodash/get";
import React, { FC, useState, createContext } from "react";
import { useQuery } from "react-query";

export const fetchCredentialsForModule = async (
  module: Module,
  moduleParams: CommissionMetaParams,
  baseUrl?: string
): Promise<ModuleMeta> => {
  const params = new URLSearchParams();

  Object.entries(moduleParams).forEach((param) => {
    params.append(param[0], param[1]);
  });

  const response = await fetch(`${baseUrl ?? ""}/api/${module}/meta?${params}`);
  const responseJSON = await response.json();

  return responseJSON.data;
};

export const CredentialContext = createContext<CredentialContextType | null>(
  null
);

const checkCredentialsForModule = (
  type: CredentialType,
  moduleMeta: ModuleMeta,
  customCredentialValue?: number
) => {
  if (!["show", "edit", "create", "delete"].includes(type)) {
    throw new Error(
      `No credential of type "${type}" for module. Valid types are "show", "edit", "create", "delete".`
    );
  }

  const credentialValue =
    customCredentialValue ?? get(moduleMeta, "module.credential");

  switch (type) {
    case "show":
      return credentialValue >= ModuleCredential.Show;
    case "edit":
      return credentialValue >= ModuleCredential.Edit;
    case "create":
      return credentialValue >= ModuleCredential.Create;
    case "delete":
      return credentialValue === ModuleCredential.Delete;
    default:
      return false;
  }
};

const checkCredentialForField = (
  type: CredentialType,
  fieldName: string,
  moduleMeta: ModuleMeta,
  isExtraField?: boolean
) => {
  if (!["show", "edit"].includes(type)) {
    throw new Error(
      `No credential of type "${type}" for ${
        isExtraField ? "extra_field" : "field"
      }. Valid types are "show", "edit".`
    );
  }

  const sourcePrefix = isExtraField ? "extra_fields" : "fields";
  const credentialValue = get(
    moduleMeta,
    `${sourcePrefix}.${fieldName}.credential`
  );

  // Jeżeli ma edit to ma też show
  if (credentialValue === FieldCredential.Edit) {
    return true;
  }

  if (type === "show" && credentialValue === FieldCredential.Show) {
    return true;
  }

  return false;
};

const checkCredentialForAttribute = (
  type: CredentialType,
  attribute: number | string,
  moduleMeta: ModuleMeta
) => {
  if (!["show", "edit"].includes(type)) {
    throw new Error(
      `No credential of type "${type}" for atrribute. Valid types are "show", "edit".`
    );
  }

  const attributeCredentialItem =
    moduleMeta?.extra_fields.attributes?.items.find(
      (attributeItem) =>
        attributeItem.id === attribute || attributeItem.uuid === attribute
    );

  const credentialValue = attributeCredentialItem?.credential;

  if (credentialValue === FieldCredential.Edit) {
    return true;
  }

  if (type === "show" && credentialValue === FieldCredential.Show) {
    return true;
  }

  return false;
};

const checkCredentialForRelation = (
  type: CredentialType,
  relation: string,
  moduleMeta: ModuleMeta
) => {
  if (!["show", "edit"].includes(type)) {
    throw new Error(
      `No credential of type "${type}" for relation. Valid types are "show", "edit".`
    );
  }

  const credentialValue = get(
    moduleMeta,
    `extra_fields.relations.properties.${relation}.credential`
  );

  // Jeżeli ma edit to ma też show
  if (credentialValue === FieldCredential.Edit) {
    return true;
  }

  if (type === "show" && credentialValue === FieldCredential.Show) {
    return true;
  }

  return false;
};

export const CredentialContextProvider: FC<CredentialContextProviderProps> = ({
  module,
  initialCredentials,
  hasCredentialsFn,
  hasModuleCredentialFn,
  hasActionCredentialFn,
  credentialFetcherFn,
  initialModuleParams = null,
  additionalKeys = null,
  useQueryOptions = {},
  children,
}) => {
  const [moduleParams, setModuleParams] = useState<ModuleParams | null>(
    initialModuleParams
  );

  const { data, isIdle, isLoading, isFetching } = useQuery<ModuleMeta>(
    ["credentials", module, moduleParams, additionalKeys],
    () =>
      credentialFetcherFn?.(module) ??
      fetchCredentialsForModule(module, {
        ...(moduleParams ?? {}),
      }),
    {
      initialData: initialCredentials as ModuleMeta,
      refetchOnWindowFocus: false,
      enabled: Boolean(module && moduleParams),
      keepPreviousData: true, // bez tego zmiana stanu react-hook-form nie przebiega poprawnie, w selektorze typu zostaje stara wartość
      ...(useQueryOptions as any),
    }
  );

  const hasCredential: HasCredential = (
    credentialIdentifier,
    objectType,
    type = "show"
  ) => {
    if (!data) {
      return false;
    }

    if (!credentialIdentifier) {
      throw new Error(
        `No credential name provided. Try something like "name".`
      );
    }

    switch (objectType) {
      case "module":
        return checkCredentialsForModule(type, data);
      case "field":
        return checkCredentialForField(
          type,
          credentialIdentifier as string,
          data
        );
      case "extraField":
        return checkCredentialForField(
          type,
          credentialIdentifier as string,
          data,
          true
        );
      case "attribute":
        return checkCredentialForAttribute(type, credentialIdentifier, data);
      case "relation":
        return checkCredentialForRelation(
          type,
          credentialIdentifier as string,
          data
        );
      default:
        return false;
    }
  };

  const hasModuleCredential = (
    credentialType: CredentialType,
    customCredentialValue?: number
  ) => {
    if (!data) {
      return false;
    }

    return checkCredentialsForModule(
      credentialType,
      data,
      customCredentialValue
    );
  };

  const hasActionCredential = (action: string) => {
    if (!data) {
      return false;
    }

    return !!data?.actions?.[action];
  };

  return (
    <CredentialContext.Provider
      value={{
        meta: data ?? null,
        setModuleParams,
        /** możliwość nadpisania funkcji sprawdzających uprawnienia np. do testów */
        hasCredential: hasCredentialsFn ? hasCredentialsFn : hasCredential,
        hasModuleCredential: hasModuleCredentialFn
          ? hasModuleCredentialFn
          : hasModuleCredential,
        hasActionCredential: hasActionCredentialFn ?? hasActionCredential,
        isLoading: isIdle || isLoading,
        isFetching: isFetching,
      }}
    >
      {children}
    </CredentialContext.Provider>
  );
};
